import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IColourSwatchCard } from '@models/IColourSwatchCard';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import AddToWishlist from '@molecules/AddToWishlist/AddToWishlist';
import ProductImageGallery from '@molecules/ProductImageGallery/ProductImageGallery';
import { createColourPng } from '@utility/helpers/createColourPng';
import useIsTouchDevice from '@utility/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { motion, useInView } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const enum ColourSwatchCardBG {
  Grey = 'tw-bg-grey-10',
  White = 'tw-bg-white',
}

interface Props extends IColourSwatchCard {
  readonly createColourImage?: boolean;
  readonly showLikeButton?: boolean;
  readonly wishlistId?: number;
}

const ColourSwatchCard = (props: Props) => {
  const {
    bgColour = ColourSwatchCardBG.Grey,
    colourCode,
    colourHexCode,
    colourName,
    description,
    images,
    link,
    mainImage,
    showLikeButton = true,
    tag,
    variant = ColourSwatchVariantEnum.Default,
    wishlistDrawerLabels,
    wishlistId,
    createColourImage = true,
  } = props;

  const [imageDataUrl, setImageDataUrl] = useState<string>();
  const [isHovered, setIsHovered] = useState(false);
  const colourRef = useRef<HTMLDivElement>(null);
  const isTouchDevice = useIsTouchDevice();
  const isDefaultVariant = variant === ColourSwatchVariantEnum.Default;
  const isDetailedVariant = variant === ColourSwatchVariantEnum.Detailed;
  const isSimpleVariant = variant === ColourSwatchVariantEnum.Simple;
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { amount: 0, once: true });

  const fetchImageDataUrl = useCallback(async () => {
    if (isTouchDevice || !colourHexCode) return;
    try {
      const dataUrl = await createColourPng(colourRef, colourHexCode);
      setImageDataUrl(dataUrl);
    } catch (error) {
      console.error(error);
    }
  }, [colourHexCode, isTouchDevice]);

  useEffect(() => {
    if (inView && createColourImage) fetchImageDataUrl();
  }, [fetchImageDataUrl, inView, createColourImage]);

  const handleTrackingEvent = useCallback(() => {
    window.dataLayer.push({
      event: 'trackingEvent_select_item',
      event_name_variable: 'select_item',
      trackingEvent_select_item: {
        item_name: colourName,
        item_category: 'colour',
        item_category3: colourHexCode,
      },
    });
  }, [colourName, colourHexCode]);

  const backgroundColorStyle = useMemo(() => {
    return colourHexCode
      ? { backgroundColor: `#${colourHexCode}` }
      : { backgroundImage: `url(${mainImage?.url})` };
  }, [colourHexCode, images]);

  return (
    <div
      className="tw-group tw-relative tw-flex tw-h-full tw-flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      ref={containerRef}
    >
      {showLikeButton && (
        <AddToWishlist
          wishlistId={wishlistId}
          card={props}
          cardId={`${link}.html`} // To match existing format
          collection={CardTypeEnum.Colours}
          wishlistDrawerLabels={wishlistDrawerLabels}
        />
      )}
      <a
        href={link}
        onClick={handleTrackingEvent}
        className="tw-relative tw-flex tw-h-full tw-flex-col"
      >
        <div
          className={classNames(
            'tw-relative',
            variant === ColourSwatchVariantEnum.Large
              ? 'tw-aspect-[4/5]'
              : 'tw-aspect-[2/3]',
          )}
        >
          {!isTouchDevice && (
            <motion.div
              ref={colourRef}
              className="tw-absolute tw-h-full tw-w-full tw-bg-center"
              style={backgroundColorStyle}
              animate={{ opacity: isHovered && images.length ? 0 : 1 }}
              exit={{ opacity: 1 }}
              initial={{ opacity: 1 }}
            >
              {imageDataUrl && (
                <img
                  className="tw-h-full tw-w-full"
                  src={imageDataUrl}
                  alt={colourName ?? `Colour code ${colourCode}`}
                />
              )}
            </motion.div>
          )}
          <motion.div
            className="tw-absolute tw-h-full tw-w-full tw-bg-center"
            style={backgroundColorStyle}
            animate={{ opacity: isHovered || isTouchDevice ? 1 : 0 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            {!isTouchDevice && images.length === 1 && images[0] ? (
              <Image
                image={images[0]}
                aspectRatio={
                  variant === ColourSwatchVariantEnum.Large ? 4 / 5 : 2 / 3
                }
                imageClasses="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-h-full tw-object-center tw-object-cover"
              />
            ) : (
              <ProductImageGallery
                showProductImage={false}
                showColourImage={isTouchDevice && !!colourHexCode}
                colourHexCode={colourHexCode}
                productImageClasses="tw-translate-y-[0] tw-h-full tw-w-full"
                imageAspectRatio={
                  variant === ColourSwatchVariantEnum.Large
                    ? 'tw-aspect-[4/5]'
                    : 'tw-aspect-[2/3]'
                }
                images={
                  isTouchDevice && mainImage && !colourHexCode
                    ? [mainImage, ...images]
                    : images
                }
                initialIndex={0}
              />
            )}
          </motion.div>

          {tag && !isSimpleVariant && (
            <div
              className={classNames(
                'tw-absolute tw-z-20 tw-flex tw-gap-[5px]',
                isDetailedVariant
                  ? 'tw-bottom-4 tw-left-4'
                  : 'tw-bottom-2 tw-left-2',
              )}
            >
              <div className="tw-grid tw-h-[25px] tw-place-items-center tw-bg-grey-80 tw-px-2">
                <Text variant="label-12" className="tw-text-white">
                  {tag}
                </Text>
              </div>
            </div>
          )}
        </div>
        {variant !== ColourSwatchVariantEnum.Large && (
          <div
            className={classNames(
              'tw-flex tw-w-full tw-flex-grow tw-flex-col tw-gap-1 tw-text-grey-100 tw-transition-colors dark:tw-bg-white',
              bgColour,
              isSimpleVariant ? 'tw-px-2.5 tw-py-xxs' : 'tw-p-xs',
            )}
          >
            <Text className="tw-block tw-text-grey-70" variant="label-12">
              {colourCode}
            </Text>

            {!isSimpleVariant && (
              <Text
                variant={
                  isTouchDevice && isDefaultVariant
                    ? 'body-18'
                    : 'heading-21-light'
                }
                className="tw-capitalize"
              >
                {colourName?.toLowerCase()}
              </Text>
            )}

            {isDetailedVariant && description && (
              <Text
                className="tw-ellipsis tw-mt-4 tw-line-clamp-3 tw-text-grey-70"
                variant="body-small"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        )}
      </a>
    </div>
  );
};

export default ColourSwatchCard;
